import React, { memo } from 'react';
import type { OptionsItem, OptionsListStyle, ItemLayout } from '@segmently/schema-models/v2';
import type { PreviewMode } from '../../../types/previewMode';
import { mapOptionsListStyleToCSS, mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { Image } from '../Image';
import { Text } from '../Text';
import styles from './BulletList.module.css';

export interface BulletListItemProps {
  item: OptionsItem;
  itemLayout?: ItemLayout;
  itemAppearance?: OptionsListStyle;
  locale: string;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
}

export const BulletListItem = memo(function BulletListItem({
  item,
  itemLayout = 'ImageTitle',
  itemAppearance,
  locale,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
}: BulletListItemProps): React.ReactElement | null {
  if (!item) {
    return null;
  }

  const { title, subtitle, image } = item;

  const containerStyle = {
    ...mapOptionsListStyleToCSS(itemAppearance),
    ...mapFrameLayoutStyles(item.layout),
  };

  const isReversed = itemLayout === 'TitleImage';
  const containerClassName = [
    styles.bulletListItem,
    isReversed ? styles.reversed : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={containerClassName}
      style={containerStyle}
      data-testid={testId}
      data-edit-path={editPath}
      role="listitem"
    >
      {image != null && (
        <div className={styles.asset}>
          <Image
            image={image}
            locale={locale}
            data-testid={testId ? `${testId}-image` : undefined}
            previewMode={previewMode}
            editPath={editPath ? `${editPath}.image` : undefined}
          />
        </div>
      )}

      <div className={styles.content}>
        <Text
          label={title}
          locale={locale}
          className={styles.title}
          data-testid={testId ? `${testId}-title` : undefined}
          editPath={editPath ? `${editPath}.title` : undefined}
        />
        {subtitle && (
          <Text
            label={subtitle}
            locale={locale}
            className={styles.subtitle}
            data-testid={testId ? `${testId}-subtitle` : undefined}
            editPath={editPath ? `${editPath}.subtitle` : undefined}
          />
        )}
      </div>
    </div>
  );
});

BulletListItem.displayName = 'BulletListItem';
